import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Share2, Play, Calendar, MapPin, Clock, Users, Lock, AlertTriangle, ChevronUp, ChevronDown, Copy, Link, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { format, addMinutes } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/Header';
import { getSettings, getTimeBetweenEvents } from '../utils/settings';
import { getSportVocab } from '../utils/sportConfig';
import { Capacitor } from '@capacitor/core';
import { getMeet } from '../utils/api';
import { getCachedUser } from '../utils/auth';
import { SentryReact } from '@/sentry';

// Get device ID for ownership check
const getDeviceId = () => {
  return localStorage.getItem('foresight_device_id') || '';
};

// Generate a simple user ID for this device/session
const getUserId = () => {
  let userId = localStorage.getItem('foresight_user_id');
  if (!userId) {
    userId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('foresight_user_id', userId);
  }
  return userId;
};

// Append a meet id to the persistent "ended meets" list. Reopening any meet
// in this list snaps its view into completed mode on the very first render.
const markMeetEnded = (meetId) => {
  if (!meetId) return;
  try {
    const arr = JSON.parse(localStorage.getItem('foresight_ended_meets') || '[]');
    const set = new Set(Array.isArray(arr) ? arr : []);
    set.add(String(meetId));
    localStorage.setItem('foresight_ended_meets', JSON.stringify([...set]));
  } catch (_) { /* ignore */ }
};

export default function MeetSchedule() {
  const { meetId } = useParams();
  const navigate = useNavigate();
  const [meet, setMeet] = useState(null);
  // "Is this meet known to have been ended in a prior session?" — read
  // synchronously from localStorage during useState init so that the
  // mount render already locks the UI into "completed" mode without any
  // flash of the back arrow / Edit buttons. The ended-meet list is
  // appended to in confirmEndMeet / autoEndMeet.
  const _priorEnded = (() => {
    try {
      const arr = JSON.parse(localStorage.getItem('foresight_ended_meets') || '[]');
      return Array.isArray(arr) && arr.includes(meetId);
    } catch (_) {
      return false;
    }
  })();
  const [isStarted, setIsStarted] = useState(_priorEnded);
  const [currentEventIndex, setCurrentEventIndex] = useState(-1);
  const [currentHeat, setCurrentHeat] = useState(1); // Track current heat within event
  const [schedule, setSchedule] = useState([]);
  // ARCHITECTURAL GUARD against the cold-restart batching race.
  //
  // Symptom history (3 prior fix attempts, all band-aids): owner's
  // countdown sometimes resets to full heat duration after a cold restart,
  // because state updates from loadMeetData (setMeet, setIsStarted,
  // setCurrentEventIndex, setCurrentHeat) can commit as separate renders
  // under WebView load instead of one batched render — and the
  // localStorage write effect / pushLiveProgress effect / updateByTime
  // interval fire on those intermediate renders with partial state.
  //
  // Real fix: NOTHING that depends on owner state runs until isHydrated is
  // true. loadMeetData (for live meets) sets it true ONLY after the
  // schedule has been built AND anchored from localStorage — i.e. when
  // the data is verifiably complete. Until then the 1-second update
  // interval doesn't initialize, the heat-tuple writer doesn't run, and
  // pushLiveProgress doesn't fire. There is no possible intermediate
  // state that produces a wrong write.
  const [isHydrated, setIsHydrated] = useState(false);
  const [exitConfirmDialog, setExitConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [endMeetDialog, setEndMeetDialog] = useState(false);
  const [manuallyEnded, setManuallyEnded] = useState(_priorEnded);
  const [forceEndDialog, setForceEndDialog] = useState(false);
  const [blockingMeetId, setBlockingMeetId] = useState(null);
  const [shareDialog, setShareDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [debugInfo, setDebugInfo] = useState(''); // Debug info for troubleshooting
  const [countdown, setCountdown] = useState(''); // Countdown to next heat/event
  const [meetComplete, setMeetComplete] = useState(_priorEnded);

  // Server-truth timeline state for spectators (and as a sanity reference for owner).
  // - liveServerSnapshot: the latest server-reported live state for this meet
  // - clockSkewMs: server_time − local_now from the last successful poll
  //   (positive ⇒ server clock ahead of device clock). Used to convert
  //   server-stamped current_event_started_at into a local-clock instant.
  const [liveServerSnapshot, setLiveServerSnapshot] = useState(null);
  const [clockSkewMs, setClockSkewMs] = useState(0);

  const userId = getUserId();
  const deviceId = getDeviceId();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const isNative = Capacitor.isNativePlatform();

  // Check if current user is the meet owner.
  //
  // Ownership semantics MIRROR the backend `_meet_owner_filter`
  // (`server.py:261`):
  //   • Logged-in caller (authUserId present): owns iff
  //     `meet.user_id === authUserId`. Device is irrelevant.
  //   • Anonymous caller (no authUserId): owns iff
  //     `meet.device_id === thisDevice` AND meet has no `user_id`.
  //
  // Production repro that motivated this fix (May 2026): user created
  // "Friday Two" on an older Android install, uninstalled (Android wipes
  // the device UUID), reinstalled, logged in with the same Google
  // account. The meet's `user_id` still matched, so the backend
  // correctly returned it via `getMeets`, but the old owner check
  // (`meet.device_id === deviceId` only) reported `isOwner=false` for
  // the new install — leaving the user looking at their own meet with
  // no Start Meet / Edit / Back buttons, effectively a spectator view
  // of a meet they had created.
  const authUserId = getCachedUser()?.user_id || null;
  const isOwner = Boolean(meet && (
    authUserId
      ? meet.user_id === authUserId
      : (deviceId && meet.device_id === deviceId && !meet.user_id)
  ));

  // Sport vocabulary lookup — Track/Swim render "Event"/"Heat", XC
  // renders "Division"/"Race". Fallback to track when meet hasn't
  // loaded yet so the very first render doesn't crash on
  // `vocab.event`. `meet.sport_type` is undefined for meets created
  // before multi-sport shipped — `getSportVocab` normalizes that to
  // track too.
  const vocab = getSportVocab(meet?.sport_type);

  // ------- Spectator live-state polling --------
  // Web spectators don't have access to the owner's localStorage, so they
  // poll the backend every 3s to follow along with the meet's progress.
  // Stress-test #6 fix (Option B): if the owner edited the meet AFTER the
  // spectator first loaded the page and BEFORE starting, the spectator's
  // `meet.events` is stale. The moment the server says the meet just went
  // live (is_active false → true), re-fetch the meet once so the event
  // list matches what the owner actually started with. We don't refetch
  // on every poll — just on the edge — to keep spectator traffic tiny.
  const prevIsActiveRef = useRef(null);
  useEffect(() => {
    if (!meetId || isOwner) return;
    let cancelled = false;

    const fetchLive = async () => {
      try {
        const res = await fetch(`${API_URL}/api/live-meet/${encodeURIComponent(meetId)}`);
        if (!res.ok) return;
        const live = await res.json();
        if (cancelled) return;
        // Capture server time → derive clock skew. Used to translate the
        // server-stamped current_event_started_at into the device's clock
        // for a drift-free spectator countdown.
        if (live.server_time) {
          const serverMs = new Date(live.server_time).getTime();
          if (!isNaN(serverMs)) {
            setClockSkewMs(serverMs - Date.now());
          }
        }
        setLiveServerSnapshot(live);
        // Detect the edge where the meet has just gone live since our last
        // poll. At that instant, re-pull the meet so any pre-start edits
        // (events added/removed/reordered) are reflected on the spectator.
        const wasActive = prevIsActiveRef.current;
        prevIsActiveRef.current = !!live.is_active;
        if (live.is_active && wasActive === false) {
          try {
            const fresh = await getMeet(meetId);
            if (!cancelled && fresh) setMeet(fresh);
          } catch (_) { /* non-fatal — spectator keeps old events */ }
        }
        if (live.is_active) {
          // Meet is running — clear any stale "complete" UI from a prior run.
          // Without this, restarting a previously-ended meet leaves spectators
          // stuck in the blue "Meet finished" state while events continue.
          setMeetComplete(false);
          if (!isStarted) setIsStarted(true);
          // For spectators, we DO NOT bind currentEventIndex / currentHeat
          // directly to the server's last-reported values. Those are only an
          // anchor; the local 1-second tick walks them forward through the
          // schedule using elapsed time. Without that, a locked-screen owner
          // would freeze spectators on whatever heat was last pushed.
          // Owners keep direct binding so their UI reflects their own taps
          // and time-based advance.
          if (isOwner) {
            if (typeof live.current_event_index === 'number') {
              setCurrentEventIndex(live.current_event_index);
            }
            if (typeof live.current_heat === 'number') {
              setCurrentHeat(live.current_heat);
            }
          }
        } else if (live.ended_at) {
          setMeetComplete(true);
          // Snap the spectator UI to the last event/heat. Without this the
          // "current event" card stays wherever the spectator was when they
          // lost connection (e.g., Event 5 / Heat 3), producing the
          // contradictory state of a blue COMPLETE banner above a green
          // "About Heat 3 of 4" card. Matches the abandoned-meet overshoot
          // path that already does the same snap.
          // INVARIANT: spectator must land on (last event, last heat) when
          // the meet is complete — see PRD.md.
          if (Array.isArray(schedule) && schedule.length > 0) {
            const lastIdx = schedule.length - 1;
            const lastHeats = schedule[lastIdx]?.heats || 1;
            setCurrentEventIndex((prev) => (prev === lastIdx ? prev : lastIdx));
            setCurrentHeat((prev) => (prev === lastHeats ? prev : lastHeats));
          }
          setCountdown('');
        } else {
          // Meet was never started — reset any leftover spectator state.
          setMeetComplete(false);
          if (isStarted) setIsStarted(false);
        }
      } catch (_) {
        /* ignore network blips — local 1s tick keeps countdown moving (2a) */
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 3000);

    // iOS Safari (and Android Chrome) aggressively throttle/pause JS timers
    // when the tab/app is backgrounded or the device is locked. When the page
    // becomes visible again, the cached snapshot is stale by however long the
    // device was locked, so refetch immediately rather than waiting up to 3s.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchLive();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetId, isOwner]);

  // Owner-only: one-shot fetch of the live state on mount. If the backend
  // says this meet has already ended (`ended_at` set), we lock the UI into
  // "completed" mode so the owner cannot edit or restart it. Without this,
  // navigating back to a finished meet from the dashboard would silently
  // re-expose the Start / Edit buttons because localStorage has no record
  // of the prior live cycle.
  useEffect(() => {
    if (!isOwner || !meetId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/live-meet/${encodeURIComponent(meetId)}`);
        if (!res.ok) return;
        const live = await res.json();
        if (cancelled) return;
        if (!live.is_active && live.ended_at) {
          setIsStarted(true);
          setMeetComplete(true);
        }
      } catch (_) {
        /* offline — leave UI as-is */
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner, meetId, API_URL]);

  // Persist the timestamp of the most recent heat boundary so a cold
  // restart can recover the in-heat countdown without needing the network.
  // pushLiveProgress already captures the same `started_at` for the
  // backend; we mirror it to localStorage so it survives an app kill even
  // when offline. Reading is done by the schedule restoration further
  // below; this effect is the single write-point.
  useEffect(() => {
    // Gated on isHydrated: see the state declaration for the full reasoning.
    // The previous attempts (sentinel + regression guards) were band-aids
    // on the partial-hydration renders. With this flag, the effect simply
    // doesn't fire until loadMeetData has finished writing the schedule
    // anchor — so there are no intermediate-render states to defend against.
    if (!isHydrated) return;
    if (!isOwner || !isStarted || !meetId || meetComplete) return;
    try {
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      if (liveMeet.meetId !== meetId && String(liveMeet.meetId) !== String(meetId)) return;
      const tuple = `${currentEventIndex}:${currentHeat}`;
      if (liveMeet.heatTuple === tuple && liveMeet.heatStartedAt) return;
      liveMeet.heatTuple = tuple;
      liveMeet.heatStartedAt = new Date().toISOString();
      liveMeet.currentEventIndex = currentEventIndex;
      liveMeet.currentHeat = currentHeat;
      localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
    } catch (_) { /* localStorage unavailable — owner just loses cold-restart precision */ }
  }, [isHydrated, isOwner, isStarted, meetId, currentEventIndex, currentHeat, meetComplete]);

  // Helper: owner pushes progress to backend so spectators see updates.
  // Failure-resilient: a skip done while offline preserves its original
  // timestamp until the push actually succeeds, so spectators get the right
  // anchor on the next poll instead of one stamped at the moment of retry.
  //
  // - confirmedTupleRef: last (event, heat) the backend has acknowledged.
  // - pendingPushRef:    {tuple, started_at} captured at the moment of skip.
  //                      Retries reuse the same started_at until 2xx.
  const confirmedTupleRef = React.useRef(null);
  const pendingPushRef = React.useRef(null);
  // Always-current reference to the owner's latest schedule array. We read
  // schedule[*].startTime/endTime from here inside pushLiveProgress so every
  // push carries the freshly-recalculated timeline to the backend. Pulling
  // from `schedule` (not `meet.events`) is critical: the recalc functions
  // (recalculateScheduleWithBothTimes etc.) update only `schedule`, leaving
  // `meet.events` frozen at the original baked-in times.
  const scheduleRef = React.useRef(null);
  React.useEffect(() => { scheduleRef.current = schedule; }, [schedule]);
  const pushLiveProgress = useCallback(async (eventIndex, heatNumber = 1) => {
    if (!isOwner || !meetId) return;
    const tuple = `${eventIndex}:${heatNumber}`;
    const isFreshSkip =
      confirmedTupleRef.current !== tuple &&
      (!pendingPushRef.current || pendingPushRef.current.tuple !== tuple);
    if (isFreshSkip) {
      // Stamp the moment of the skip exactly once. Heartbeat retries will
      // resend this same started_at until the backend confirms.
      //
      // Resume-from-background guard: when Android (or iOS) kills the
      // WebView under memory pressure and re-creates it on resume,
      // confirmedTupleRef resets to null. The local UI restores the
      // mid-heat countdown from localStorage (Invariant #5), but THIS
      // function would then push `started_at = new Date()` to the backend,
      // which would reset every spectator's countdown back to the full
      // heat duration. Repro: heavy multitasking (YouTube + Maps + video
      // capture) backgrounds the owner app; on return owner's countdown
      // is correct but spectator jumps from e.g. 0:58 back to 3:30.
      //
      // Fix: if localStorage already has a heatStartedAt for THIS exact
      // tuple on THIS meet, reuse it. New skips have no matching stamp
      // (the localStorage write effect hasn't run yet for the new tuple)
      // so they fall through to `new Date()` as before.
      let stampedAt = new Date().toISOString();
      try {
        const persisted = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
        if (
          persisted &&
          String(persisted.meetId) === String(meetId) &&
          persisted.heatTuple === tuple &&
          persisted.heatStartedAt
        ) {
          stampedAt = persisted.heatStartedAt;
        }
      } catch (_) { /* corrupt localStorage — fall back to now */ }
      pendingPushRef.current = { tuple, started_at: stampedAt };
    }
    const body = {
      current_event_index: eventIndex,
      current_heat: heatNumber,
    };
    if (pendingPushRef.current && pendingPushRef.current.tuple === tuple) {
      body.current_event_started_at = pendingPushRef.current.started_at;
    }
    // Piggy-back the owner's current per-event schedule so spectators
    // render accurate wall-clock times. Only send the two fields we need;
    // sending the entire events payload every tick would be wasteful.
    const liveSchedule = scheduleRef.current;
    if (Array.isArray(liveSchedule) && liveSchedule.length > 0) {
      body.schedule = liveSchedule.map((e) => ({
        startTime: e.startTime ? new Date(e.startTime).toISOString() : null,
        endTime: e.endTime ? new Date(e.endTime).toISOString() : null,
      }));
    }
    try {
      const res = await fetch(`${API_URL}/api/live-meet/${encodeURIComponent(meetId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        confirmedTupleRef.current = tuple;
        // Only clear the pending push if it was for this same tuple — a
        // newer skip might already be queued and shouldn't be discarded.
        if (pendingPushRef.current && pendingPushRef.current.tuple === tuple) {
          pendingPushRef.current = null;
        }
      }
      // On non-2xx we leave pendingPushRef alone so the heartbeat retries.
    } catch (_) {
      // Network failure — pending state stays put for retry.
    }
  }, [isOwner, meetId, API_URL]);

  // Owner: whenever the local current event/heat changes during a live meet,
  // push it to the backend so spectators see the same state.
  useEffect(() => {
    if (!isHydrated) return;
    if (!isOwner || !isStarted) return;
    pushLiveProgress(currentEventIndex, currentHeat);
  }, [isHydrated, isOwner, isStarted, currentEventIndex, currentHeat, pushLiveProgress]);

  // Owner: 10-second heartbeat that re-pushes the current (event, heat) state.
  // This is the offline-recovery safety net — if a push failed because the
  // device was offline at the moment the owner advanced, the next heartbeat
  // (after network is restored) silently catches the spectator state up.
  // Heartbeats DO NOT carry current_event_started_at, so they never overwrite
  // the server-truth start timestamp set by the original tuple-change push.
  useEffect(() => {
    if (!isOwner || !isStarted) return;
    const beat = setInterval(() => {
      pushLiveProgress(currentEventIndex, currentHeat);
    }, 10000);
    return () => clearInterval(beat);
  }, [isOwner, isStarted, currentEventIndex, currentHeat, pushLiveProgress]);

  // Owner: also retry immediately when the device transitions back online —
  // covers the case where the offline gap is shorter than the heartbeat.
  useEffect(() => {
    if (!isOwner || !isStarted) return;
    const onBackOnline = () => pushLiveProgress(currentEventIndex, currentHeat);
    window.addEventListener('online', onBackOnline);
    return () => window.removeEventListener('online', onBackOnline);
  }, [isOwner, isStarted, currentEventIndex, currentHeat, pushLiveProgress]);

  // Sentry: tag every event/crash with the user's current role + meet state so
  // launch-day crashes can be instantly filtered by "owner during live meet"
  // vs "spectator viewing completed meet" etc. No-ops on web (initSentry
  // early-returns there — see Invariant #10).
  useEffect(() => {
    const meetState = meetComplete
      ? 'completed'
      : isStarted
        ? 'live'
        : 'pre-start';
    SentryReact.setTag('meet_state', meetState);
    SentryReact.setTag('user_role', isOwner ? 'owner' : 'spectator');
  }, [isOwner, isStarted, meetComplete]);

  // Generate shareable URL for the meet
  const getShareUrl = () => {
    // In Capacitor native app, window.location.origin is "https://localhost"
    // (or "capacitor://localhost"), which is not a valid public link. Fall back
    // to the configured public web URL so spectators get a real, openable link.
    const publicWebUrl = process.env.REACT_APP_PUBLIC_WEB_URL;
    let baseUrl = window.location.origin;
    if (isNative || /localhost/i.test(baseUrl)) {
      baseUrl = publicWebUrl || process.env.REACT_APP_BACKEND_URL || baseUrl;
    }
    // Strip any trailing slash to avoid "//meet/..."
    baseUrl = baseUrl.replace(/\/+$/, '');
    return `${baseUrl}/meet/${meetId}`;
  };

  // Helper for "critical" warnings — orange background, longer linger.
  // Used for things the user really needs to read (network blockers, etc.)
  // and shouldn't be lost in a 2-second flash. Centralised here so we
  // never accidentally style a critical warning the same as a routine
  // success toast.
  const criticalToast = (message) => {
    toast(message, {
      duration: 8000,
      style: {
        background: '#f97316', // tailwind orange-500
        color: '#ffffff',
        border: '1px solid #c2410c',
        fontWeight: 600,
      },
    });
  };

  // Helper used by both Share and Copy: confirms the meet actually exists
  // on the server before we hand out a public URL. A meet created or
  // edited offline lives only on this device until the offline-sync
  // queue uploads it — that can take a few seconds after reconnect, and
  // `navigator.onLine` flipping to true doesn't mean THIS meet has
  // synced yet. Skip this check when offline (we already block earlier
  // with a different message) or when meetId looks invalid.
  const verifyMeetOnServer = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/meets/${encodeURIComponent(meetId)}`,
        { method: 'GET', cache: 'no-store' }
      );
      return res.ok;
    } catch (_) {
      // Network blip — treat as "not confirmed" so we don't hand out a
      // URL we can't verify. Conservative on purpose.
      return false;
    }
  };

  // Handle share button click
  const handleShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Spectator URLs only work once the meet exists on the server. A
      // meet created or edited offline lives only on this device until
      // sync runs, so a shared link would 404 ("Meet not found"). Block
      // share entirely until the device is back online.
      criticalToast('You\'re offline. Reconnect to share the meet link.');
      return;
    }
    // Even online, the meet may not have finished uploading from the
    // offline-sync queue yet. Verify it's on the server first.
    const exists = await verifyMeetOnServer();
    if (!exists) {
      criticalToast('Meet is still syncing to the cloud. Try again in a few seconds.');
      return;
    }
    const shareUrl = getShareUrl();
    const shareData = {
      title: meet?.name || 'Track Meet',
      text: `Check out ${meet?.name} at ${meet?.venue} on ${meet?.date ? format(new Date(meet.date), 'MMM dd, yyyy') : 'TBD'}`,
      url: shareUrl,
    };

    // Try native share API first (works on mobile and some desktop browsers)
    if (navigator.share && (isNative || /mobile|android|iphone|ipad/i.test(navigator.userAgent))) {
      try {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
        // Fall through to dialog
      }
    }
    
    // Show share dialog for desktop/fallback
    setShareDialog(true);
  };

  // Copy link to clipboard
  const copyShareLink = async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Same rationale as handleShare — a link copied while offline may
      // point at a meet the server has never heard of.
      criticalToast('You\'re offline. Reconnect to copy the meet link.');
      return;
    }
    const exists = await verifyMeetOnServer();
    if (!exists) {
      criticalToast('Meet is still syncing to the cloud. Try again in a few seconds.');
      return;
    }
    const shareUrl = getShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy link');
    }
  };

  // Load meet data
  const [meetNotFound, setMeetNotFound] = useState(false);
  
  // Use a key to force re-render when returning from edit
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Reset the hydration gate whenever we navigate between meets. The
  // component instance is reused across URL changes (React Router param),
  // so without this reset, an isHydrated=true from the previous meet would
  // briefly let the old effects fire against the new meet's still-loading
  // state. Mounting fresh on first load is already covered (useState default).
  useEffect(() => {
    setIsHydrated(false);
  }, [meetId]);

  useEffect(() => {
    const loadMeetData = async () => {
      // First check localStorage for the meet (immediate fallback)
      const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
      const localMeet = localMeets.find(m => String(m.id) === String(meetId));
      
      try {
        // Try to load from backend API. If it returns null on first try
        // (sync race — owner just created the meet), retry twice with backoff.
        let foundMeet = await getMeet(meetId);
        for (let attempt = 1; attempt <= 2 && !foundMeet; attempt += 1) {
          await new Promise((r) => setTimeout(r, 1500 * attempt));
          foundMeet = await getMeet(meetId);
        }
        
        if (foundMeet) {
          setMeet(foundMeet);
          
          // Check localStorage for live meet state
          const liveMeetData = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          const isLive = liveMeetData.meetId === meetId || String(liveMeetData.meetId) === String(meetId);
          
          if (isLive) {
            // Meet is live - restore state
            setIsStarted(true);
            const savedIndex = liveMeetData.currentEventIndex || 0;
            setCurrentEventIndex(savedIndex);
            // Restore current heat (default to 1)
            setCurrentHeat(liveMeetData.currentHeat || 1);
          } else {
            setIsStarted(false);
            setCurrentEventIndex(-1);
            setCurrentHeat(1);
          }
        } else if (localMeet) {
          // API didn't find it but we have it locally
          setMeet(localMeet);
          
          const liveMeetData = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          const isLive = liveMeetData.meetId === meetId || String(liveMeetData.meetId) === String(meetId);
          
          if (isLive) {
            setIsStarted(true);
            setCurrentEventIndex(liveMeetData.currentEventIndex || 0);
            setCurrentHeat(liveMeetData.currentHeat || 1);
          } else {
            setIsStarted(false);
            setCurrentEventIndex(-1);
            setCurrentHeat(1);
          }
        } else {
          // Meet not found anywhere
          setMeetNotFound(true);
          // Clean up any stale live meet reference
          const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
            localStorage.removeItem('foresight_live_meet');
          }
        }
      } catch (error) {
        console.error('Failed to load meet:', error);
        // Fallback to localStorage
        if (localMeet) {
          setMeet(localMeet);
          
          const liveMeetData = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          const isLive = liveMeetData.meetId === meetId || String(liveMeetData.meetId) === String(meetId);
          
          if (isLive) {
            setIsStarted(true);
            setCurrentEventIndex(liveMeetData.currentEventIndex || 0);
            setCurrentHeat(liveMeetData.currentHeat || 1);
          }
        } else {
          setMeetNotFound(true);
        }
      } finally {
        // ARCHITECTURAL GATE: flip isHydrated AFTER every other setState
        // in this function. React 18 auto-batches the sync calls into one
        // render; if batching breaks under WebView load, React still
        // processes setState calls in order, so this commits LAST. That
        // guarantees: when an effect first sees isHydrated=true, all of
        // meet / isStarted / currentEventIndex / currentHeat are also
        // committed to their hydrated values. No intermediate-render
        // state is reachable while isHydrated is true.
        setIsHydrated(true);
      }
    };
    
    loadMeetData();
  }, [meetId, refreshKey]);
  
  // Generate schedule when meet data changes
  // Track if this is a refresh (returning from edit) to handle schedule recalculation
  const prevRefreshKey = React.useRef(refreshKey);
  
  useEffect(() => {
    if (!meet) return;

    // Check if meet is live BEFORE generating schedule
    const liveMeetData = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
    const isLive = liveMeetData.meetId === meetId || String(liveMeetData.meetId) === String(meetId);

    if (isLive && (liveMeetData.currentEventIndex >= 0 || liveMeetData.currentEventIndex === 0)) {
      // Meet is live. Build the base schedule and re-anchor it from
      // localStorage in ONE synchronous setSchedule call. The old
      // implementation called generateScheduleFromMeet() (which did its
      // own setSchedule) and then ran the re-anchor inside a
      // setTimeout(10ms) functional update. That tiny setTimeout was a
      // race: on cold restart the 1-second updateByTime tick could fire
      // BEFORE the timeout, seeing the un-anchored baked-in startTimes
      // (meet.date + meet.startTime — possibly hours ago). elapsedMinutes
      // was then huge, walk-forward fired, owner teleported several
      // events ahead, spectator followed. The updateByTime self-heal
      // (Invariant #5) catches that race after the fact; removing the
      // setTimeout eliminates the race itself.

      let eventIndex = liveMeetData.currentEventIndex || 0;
      let savedHeat = liveMeetData.currentHeat || 1;

      // Validate that currentEventIndex is within bounds
      const eventCount = meet.events ? meet.events.reduce((count, event) => {
        let genders = 0;
        if (event.showGirls) genders++;
        if (event.showBoys) genders++;
        if (event.combined) genders = 1;
        return count + genders;
      }, 0) : 0;

      if (eventIndex >= eventCount && eventCount > 0) {
        eventIndex = eventCount - 1;
        savedHeat = 1;
        setCurrentEventIndex(eventIndex);
        setCurrentHeat(savedHeat);
        liveMeetData.currentEventIndex = eventIndex;
        liveMeetData.currentHeat = savedHeat;
        localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeetData));
      }

      // Build the baseline schedule synchronously. Use the existing
      // schedule state if we already have one (e.g. returning from an
      // edit and re-running this effect) to avoid a brief visual reset.
      const baseline = schedule.length > 0 ? schedule : buildScheduleItems(meet);
      if (!baseline.length) return;

      const currentEvent = baseline[eventIndex];
      if (!currentEvent) return;

      // Clamp savedHeat to actual heats in event
      const clampedHeat = Math.min(savedHeat, currentEvent.heats);
      if (clampedHeat !== savedHeat) {
        setCurrentHeat(clampedHeat);
        liveMeetData.currentHeat = clampedHeat;
        localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeetData));
      }

      const timeBetweenEventsStr = getTimeBetweenEvents(meet.type);
      const timeBetweenEvents = parseFloat(timeBetweenEventsStr) || 0;
      const durationPerHeat = currentEvent.duration / currentEvent.heats;

      // Cold-restart anchor strategy (tiered, deterministic, NEVER falls
      // back to "starts now" while we still have any historical data):
      //
      //   1. PREFER `heatStartedAt` — the per-heat timestamp written by the
      //      localStorage write effect on each heat boundary. This is the
      //      most precise: it tells us exactly when the current heat
      //      started.
      //
      //   2. FALL BACK to `startedAt` — the meet-start timestamp written
      //      by startMeet. From this we can deterministically compute when
      //      the current heat started:
      //         currentEventStart = startedAt + Σ(prior events' durations)
      //         currentHeatStart  = currentEventStart + (heat-1)*durPerHeat
      //      This loses sub-heat precision but never lies about elapsed
      //      time. Walk-forward in updateByTime will then either correctly
      //      land us in the right heat or advance past stale heats.
      //
      //   3. LAST RESORT only — `new Date()`. Reached ONLY if both
      //      heatStartedAt AND startedAt are missing, which would mean
      //      the localStorage entry is corrupt. Logged to Sentry.
      const expectedTuple = `${eventIndex}:${clampedHeat}`;
      const stampMatches = liveMeetData.heatTuple === expectedTuple
        && !!liveMeetData.heatStartedAt;
      const heatStamp = stampMatches ? new Date(liveMeetData.heatStartedAt) : null;
      const meetStartedAtStamp = liveMeetData.startedAt
        ? new Date(liveMeetData.startedAt)
        : null;

      let heatStart;
      let anchorSource;
      if (heatStamp && !isNaN(heatStamp.getTime())) {
        heatStart = heatStamp;
        anchorSource = 'heatStartedAt';
      } else if (meetStartedAtStamp && !isNaN(meetStartedAtStamp.getTime())) {
        // Walk forward through baseline up to (but not including) eventIndex,
        // summing each prior event's duration + the gap. baseline at this
        // point may be either the fresh buildScheduleItems() output (whose
        // startTimes are baked-in predictions from meet.date+meet.startTime
        // — useless to us) OR the previously-anchored schedule. Either way
        // we trust `duration` (an intrinsic property of the event) and
        // `timeBetweenEvents`, not the volatile startTime fields.
        let cursor = meetStartedAtStamp.getTime();
        for (let i = 0; i < eventIndex; i++) {
          cursor += (baseline[i].duration + timeBetweenEvents) * 60000;
        }
        const eventStartFromMeet = new Date(cursor);
        heatStart = new Date(
          eventStartFromMeet.getTime() + (clampedHeat - 1) * durationPerHeat * 60000
        );
        anchorSource = 'startedAt_walked';
      } else {
        heatStart = new Date();
        anchorSource = 'now_fallback';
        // Surface this to Sentry so we know if a user ever reaches this
        // truly-corrupt-localStorage branch in the wild.
        try {
          if (typeof window !== 'undefined' && window.Sentry?.captureMessage) {
            window.Sentry.captureMessage(
              'Live restoration fell back to new Date() — both heatStartedAt and startedAt missing',
              {
                level: 'warning',
                extra: {
                  meetId,
                  expectedTuple,
                  storedHeatTuple: liveMeetData.heatTuple,
                  storedKeys: Object.keys(liveMeetData),
                },
              }
            );
          }
        } catch (_) { /* Sentry not configured */ }
      }

      // Sentry breadcrumb to help debug future cold-restart regressions.
      try {
        if (typeof window !== 'undefined' && window.Sentry?.addBreadcrumb) {
          window.Sentry.addBreadcrumb({
            category: 'live-restore',
            message: `anchor=${anchorSource}`,
            data: {
              eventIndex, clampedHeat, expectedTuple,
              storedHeatTuple: liveMeetData.heatTuple,
              storedHeatStartedAt: liveMeetData.heatStartedAt,
              storedMeetStartedAt: liveMeetData.startedAt,
              resolvedHeatStartISO: heatStart.toISOString(),
            },
          });
        }
      } catch (_) { /* ignore */ }

      const currentEventStartTime = addMinutes(heatStart, -(clampedHeat - 1) * durationPerHeat);
      const currentEventEndTime = addMinutes(currentEventStartTime, currentEvent.heats * durationPerHeat);

      let nextStartTime = addMinutes(currentEventEndTime, timeBetweenEvents);

      const anchored = baseline.map((item, idx) => {
        if (idx < eventIndex) {
          return item;
        } else if (idx === eventIndex) {
          return {
            ...item,
            startTime: new Date(currentEventStartTime),
            endTime: new Date(currentEventEndTime),
          };
        } else {
          const eventStart = new Date(nextStartTime);
          const eventEnd = addMinutes(nextStartTime, item.duration);
          nextStartTime = addMinutes(eventEnd, timeBetweenEvents);
          return {
            ...item,
            startTime: eventStart,
            endTime: eventEnd,
          };
        }
      });

      // ONE setSchedule call. No race possible — the next render and
      // every subsequent tick see the fully-anchored schedule. The
      // architectural isHydrated gate (set in loadMeetData's finally
      // block) ensures effects only fire AFTER this anchored schedule is
      // in place.
      setSchedule(anchored);
    } else {
      // Meet is NOT live - generate fresh schedule from baked-in times.
      generateScheduleFromMeet(meet);
    }

    prevRefreshKey.current = refreshKey;
  }, [meet, refreshKey, meetId, schedule.length]);
  
  // Reload when window gains focus (after returning from edit).
  // Owner only — spectators don't need this (they're already on a 3s poll
  // plus a visibilitychange refetch), and on mobile Safari, tapping whitespace
  // can fire spurious focus events that briefly hid the live banner.
  useEffect(() => {
    if (!isOwner) return;
    const handleFocus = () => {
      // Increment refresh key to trigger reload
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isOwner]);

  // Validate currentHeat against actual heats in current event
  // This handles cases where heats were changed during edit (safety net)
  useEffect(() => {
    if (!isStarted || !schedule.length || currentEventIndex < 0) return;
    
    const currentEvent = schedule[currentEventIndex];
    if (!currentEvent) return;
    
    // If currentHeat exceeds the event's actual heats, clamp it
    if (currentHeat > currentEvent.heats) {
      const clampedHeat = currentEvent.heats;
      setCurrentHeat(clampedHeat);
      
      // Update localStorage
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
        liveMeet.currentHeat = clampedHeat;
        localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
      }
    }
  }, [isStarted, schedule, currentEventIndex, currentHeat, meetId]);

  // Auto-start meet at scheduled time
  const isStartedRef = React.useRef(isStarted);
  const manuallyEndedRef = React.useRef(manuallyEnded);
  
  // Keep refs in sync
  React.useEffect(() => {
    isStartedRef.current = isStarted;
  }, [isStarted]);
  
  React.useEffect(() => {
    manuallyEndedRef.current = manuallyEnded;
  }, [manuallyEnded]);

  // Automatically update current heat and event based on real time
  // OWNER ONLY — spectators get state from backend polling, so running this
  // for them would cause the local clock and the polled state to fight.
  useEffect(() => {
    // ARCHITECTURAL GUARD: the 1-second update interval is PHYSICALLY
    // LOCKED until isHydrated flips to true. loadMeetData (for live meets)
    // sets isHydrated only after the schedule has been built AND anchored
    // from localStorage. Until then, no tick runs, no walk-forward can
    // fire, and pushLiveProgress cannot stamp a wrong anchor — so the
    // cold-restart "countdown resets to start of heat" race is structurally
    // impossible, not merely guarded against.
    if (!isHydrated) return;
    if (!isStarted || !schedule.length) return;
    if (!isOwner) return;

    // Don't run updates if meet is already complete
    if (meetComplete) return;

    const updateByTime = () => {
      const now = new Date();
      
      if (currentEventIndex < 0 || !schedule.length) {
        setDebugInfo('No event index or schedule');
        setCountdown('');
        return;
      }
      
      const currentEvent = schedule[currentEventIndex];
      if (!currentEvent) {
        setDebugInfo('No current event');
        setCountdown('');
        return;
      }
      
      const eventStartTime = new Date(currentEvent.startTime);
      const totalHeats = currentEvent.heats;
      const durationPerHeat = currentEvent.duration / totalHeats; // in minutes
      
      // Skip if startTime is invalid (not a real date)
      if (isNaN(eventStartTime.getTime())) {
        setDebugInfo('Invalid start time: ' + currentEvent.startTime);
        setCountdown('');
        return;
      }
      
      // Calculate which heat we should be on based on elapsed time
      const elapsedMinutes = (now.getTime() - eventStartTime.getTime()) / (1000 * 60);

      // Self-heal: if the current event's startTime is in the future
      // (negative elapsed) the schedule was loaded with the original baked-in
      // predicted times instead of being re-anchored to "now". This happens
      // after the owner re-opens the app following a long offline period —
      // the 10ms setTimeout race in the live-restoration effect occasionally
      // loses to the browser's first paint on cold load. Re-anchor the
      // current event to start now so the countdown reflects reality, and
      // bail this tick — next tick will compute against the corrected time.
      // Threshold of -0.05 min (~3 sec) avoids triggering on tiny clock
      // drift; -10 min in the user's repro will easily exceed it.
      if (elapsedMinutes < -0.05) {
        recalculateScheduleFromEvent(currentEventIndex, new Date());
        // Persist the corrected currentHeat baseline as well (heat 1 of the
        // anchor we just stamped). Without this, the user sees the heat
        // briefly contradict the freshly-anchored startTime.
        setCountdown('');
        return;
      }

      // Symmetric self-heal for the PAST direction. Same 10ms re-anchor
      // race, opposite symptom: the schedule still has the baked-in
      // startTimes (meet.date + meet.startTime — possibly hours ago), so
      // elapsedMinutes for the current event is huge and walk-forward
      // teleports the owner several events ahead. Repro: cold-kill app
      // mid-meet, reopen, tap Continue Live Meet → owner jumps from
      // (Event 3, Heat 4) to (Event 6, Heat 5) with a "Caught up: 200m
      // (Girls), Heat 5" toast and the spectator follows via
      // pushLiveProgress. See PRD Invariant #3 (sync logic) and #5
      // (cold-restart anchor recovery).
      //
      // Diagnostic: if localStorage.foresight_live_meet.heatStartedAt for
      // the CURRENT tuple says we're sanely mid-heat (elapsed in this
      // heat <= durationPerHeat + 30s slack), but the schedule disagrees
      // (elapsedMinutes spans far past the current event), the schedule
      // is unrooted. Re-anchor it from the persisted stamp and bail; the
      // next tick computes against the corrected baseline. This must run
      // BEFORE both the walk-forward branch (catastrophic teleport) and
      // the heat-advance branch (silent one-off skip).
      try {
        const persisted = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
        if (
          persisted &&
          (persisted.meetId === meetId || String(persisted.meetId) === String(meetId)) &&
          persisted.heatTuple === `${currentEventIndex}:${currentHeat}` &&
          persisted.heatStartedAt
        ) {
          const persistedHeatStart = new Date(persisted.heatStartedAt);
          if (!isNaN(persistedHeatStart.getTime())) {
            const persistedElapsedThisHeat =
              (now.getTime() - persistedHeatStart.getTime()) / 60000;
            const expectedElapsed =
              (currentHeat - 1) * durationPerHeat + persistedElapsedThisHeat;
            const scheduleSkew = elapsedMinutes - expectedElapsed;
            if (
              persistedElapsedThisHeat >= 0 &&
              persistedElapsedThisHeat <= durationPerHeat + 0.5 &&
              scheduleSkew > durationPerHeat
            ) {
              // Re-anchor: current event starts (currentHeat-1) heats
              // before the persisted heat-start stamp.
              const correctedEventStart = addMinutes(
                persistedHeatStart,
                -(currentHeat - 1) * durationPerHeat
              );
              recalculateScheduleFromEvent(currentEventIndex, correctedEventStart);
              setCountdown('');
              return;
            }
          }
        }
      } catch (_) {
        /* corrupt localStorage — fall through, walk-forward will handle */
      }
      
      // Calculate time until next heat or event
      const currentHeatEndMinutes = currentHeat * durationPerHeat;
      const timeUntilNextMinutes = currentHeatEndMinutes - elapsedMinutes;
      
      // Check if this is the last heat of the last event
      const isLastHeatOfLastEvent = currentHeat >= totalHeats && currentEventIndex >= schedule.length - 1;
      
      // Format countdown
      if (isLastHeatOfLastEvent) {
        // Last heat of last event - show special message (no countdown or "Advancing...")
        setCountdown(`Last ${vocab.heat} of Meet!`);
      } else if (timeUntilNextMinutes > 0) {
        const mins = Math.floor(timeUntilNextMinutes);
        const secs = Math.floor((timeUntilNextMinutes - mins) * 60);
        const isLastHeat = currentHeat >= totalHeats;
        const label = isLastHeat ? `Next ${vocab.event.toLowerCase()} in about` : `Next ${vocab.heat.toLowerCase()} in about`;
        setCountdown(`${label} ${mins}:${secs.toString().padStart(2, '0')}`);
      } else {
        setCountdown('Advancing...');
      }
      
      // Debug info
      const debugStr = `Start: ${format(eventStartTime, 'HH:mm:ss')} | Now: ${format(now, 'HH:mm:ss')} | Elapsed: ${elapsedMinutes.toFixed(1)}m | Per heat: ${durationPerHeat.toFixed(1)}m | Calc heat: ${Math.floor(elapsedMinutes / durationPerHeat) + 1} | Total: ${totalHeats}`;
      setDebugInfo(debugStr);
      
      // Skip if elapsed time is negative (event hasn't started yet in theory)
      if (elapsedMinutes < 0) {
        return;
      }
      
      const calculatedHeat = Math.floor(elapsedMinutes / durationPerHeat) + 1;
      
      if (calculatedHeat > totalHeats) {
        // We've overshot the current event's heats. Two distinct cases:
        //   (a) Normal single advance — owner finished the event in real
        //       time, the next 1-second tick should bump to next event.
        //   (b) Owner was offline (locked tablet, no WiFi) for a long
        //       stretch. The elapsed delta is huge and spans many events.
        // Walk forward through the schedule to determine which case we're
        // in, exactly like the spectator does. This gives us:
        //   • The correct landing event/heat if we're still mid-meet, OR
        //   • A clear "past the end with N minutes of overshoot" signal,
        //     which lets us mark the meet complete reliably when the
        //     owner abandoned it offline (no manual end tap possible).
        let walkIdx = currentEventIndex;
        let walkHeat = currentHeat;
        // Time leftover after fully consuming the current event. NOTE:
        // elapsedMinutes is from the event's startTime (= start of heat 1),
        // so we subtract the WHOLE event's duration, not just the
        // remaining-from-currentHeat slice. Earlier versions of this code
        // subtracted (totalHeats - currentHeat + 1) * durationPerHeat,
        // which double-counted the heats prior to currentHeat as
        // "unconsumed" and caused the walk-forward to land deep inside
        // the next event (e.g., heat 9 of 10) instead of at heat 1.
        let unconsumedMinutes = elapsedMinutes - totalHeats * durationPerHeat;
        if (unconsumedMinutes < 0) unconsumedMinutes = 0;
        walkIdx += 1;
        walkHeat = 1;
        while (walkIdx < schedule.length) {
          const ev = schedule[walkIdx];
          const evHeats = ev.heats || 1;
          const evDurPerHeat = (ev.duration || 0) / evHeats;
          const evTotalMin = evHeats * evDurPerHeat;
          if (evTotalMin <= 0) {
            // Defensive — zero-duration event, just skip past it.
            walkIdx += 1;
            continue;
          }
          if (unconsumedMinutes < evTotalMin) {
            walkHeat = Math.floor(unconsumedMinutes / evDurPerHeat) + 1;
            walkHeat = Math.min(Math.max(walkHeat, 1), evHeats);
            break;
          }
          unconsumedMinutes -= evTotalMin;
          walkIdx += 1;
        }

        if (walkIdx >= schedule.length) {
          // Walked past the end of the schedule. Auto-complete the meet.
          // Force currentHeat to the last event's last heat so the UI
          // shows "Heat N of N" + "Meet Complete!" consistently rather
          // than the previous bug of "Heat 1 of N + Meet Complete!".
          const lastIdx = schedule.length - 1;
          if (lastIdx >= 0) {
            const lastEv = schedule[lastIdx];
            const lastHeats = lastEv.heats || 1;
            setCurrentEventIndex(lastIdx);
            setCurrentHeat(lastHeats);
            const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
            if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
              liveMeet.currentEventIndex = lastIdx;
              liveMeet.currentHeat = lastHeats;
              localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
            }
          }
          // Only mark the meet complete + fire autoEndMeet when the device
          // is online. Reasoning: if we're offline, we cannot tell the
          // backend the meet has ended, and a coach who is genuinely
          // mid-meet (e.g. WiFi died on a long event) shouldn't have the
          // local app silently flip to "Meet Complete" — that's the
          // failure mode that produced the May 2026 production repro
          // (meet > 1.5h, app offline, no auto-end, but the *next* time
          // an offline walk-forward overshoots, we'd compound the issue).
          // When the device next comes online, either (a) updateByTime
          // ticks again and this branch fires for real, or (b) the
          // App-level online-event handler flushes any genuinely-ended
          // state. Until then: clamp to last heat, stay LIVE.
          const onlineNow = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
          if (!meetComplete && onlineNow) {
            setMeetComplete(true);
            autoEndMeet();
          }
        } else if (walkIdx === currentEventIndex + 1 && walkHeat === 1) {
          // Normal advance into the very next event at heat 1. Re-anchor
          // its startTime to "now" minus any partial elapsed time that
          // already passed inside heat 1 of the next event during an
          // offline stretch. `unconsumedMinutes` at this point is the
          // leftover after the prior event fully consumed its heats;
          // since walkHeat === 1, that leftover is the elapsed time
          // already accumulated in heat 1 of the next event. Without
          // this subtraction the countdown for the new event resets to
          // full duration even though the heat is partly elapsed.
          const nextEvent = schedule[walkIdx];
          const nextHeats = (nextEvent && nextEvent.heats) || 1;
          const nextDurPerHeat = (nextEvent && nextEvent.duration ? nextEvent.duration : 0) / nextHeats;
          let remainderMinInHeat = unconsumedMinutes;
          if (remainderMinInHeat < 0) remainderMinInHeat = 0;
          if (nextDurPerHeat > 0 && remainderMinInHeat > nextDurPerHeat) {
            remainderMinInHeat = nextDurPerHeat;
          }
          const heatStartInstant = addMinutes(now, -remainderMinInHeat);
          try {
            const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
            if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
              liveMeet.currentEventIndex = walkIdx;
              liveMeet.currentHeat = 1;
              liveMeet.heatTuple = `${walkIdx}:1`;
              liveMeet.heatStartedAt = heatStartInstant.toISOString();
              localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
            }
          } catch (_) { /* corrupt localStorage — pushLiveProgress falls back to now */ }
          setCurrentEventIndex(walkIdx);
          setCurrentHeat(1);
          pushLiveProgress(walkIdx, 1);
          recalculateScheduleFromEvent(walkIdx, heatStartInstant);
          if (nextEvent) {
            toast.info(`Now: ${nextEvent.name} (${nextEvent.gender})`);
          }
        } else {
          // Multi-event jump (offline catch-up that didn't go past the
          // end). Land directly at (walkIdx, walkHeat) and re-anchor that
          // event so future ticks compute correctly.
          //
          // CRITICAL: preserve the modulo/remainder elapsed time inside
          // the landing heat. `unconsumedMinutes` at this point is the
          // total elapsed time within event walkIdx; subtracting the
          // fully-completed prior heats yields how far we are INTO the
          // current heat (0 <= remainderMinInHeat < evDurPerHeat). Without
          // this, the countdown resets to the full heat duration even
          // though the heat is partially elapsed — repro: cold-kill app
          // mid-heat 3 of event 5, reopen 10 min later, owner correctly
          // lands on heat 5 but timer shows 2:30 (full) instead of 0:30.
          const ev = schedule[walkIdx];
          const evHeats = ev.heats || 1;
          const evDurPerHeat = (ev.duration || 0) / evHeats;
          let remainderMinInHeat = unconsumedMinutes - (walkHeat - 1) * evDurPerHeat;
          if (remainderMinInHeat < 0) remainderMinInHeat = 0;
          if (remainderMinInHeat > evDurPerHeat) remainderMinInHeat = evDurPerHeat;
          // The instant the landing heat actually started.
          const heatStartInstant = addMinutes(now, -remainderMinInHeat);
          // Event walkIdx started (walkHeat-1) heats before heatStartInstant.
          const eventStartTime = addMinutes(heatStartInstant, -(walkHeat - 1) * evDurPerHeat);
          const eventEndTime = addMinutes(eventStartTime, evHeats * evDurPerHeat);
          // Pre-write localStorage with the precise heat anchor BEFORE
          // setState/pushLiveProgress so:
          //   (a) the localStorage write effect (gated on currentEventIndex/
          //       currentHeat) sees the matching tuple+heatStartedAt and
          //       short-circuits its "stamp now" overwrite (line ~267).
          //   (b) pushLiveProgress's fresh-skip branch reads back our
          //       precise heatStartedAt and pushes it as
          //       current_event_started_at to the backend, so spectators
          //       sync to the correct mid-heat moment instead of a full
          //       heat duration ahead.
          try {
            const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
            if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
              liveMeet.currentEventIndex = walkIdx;
              liveMeet.currentHeat = walkHeat;
              liveMeet.heatTuple = `${walkIdx}:${walkHeat}`;
              liveMeet.heatStartedAt = heatStartInstant.toISOString();
              localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
            }
          } catch (_) { /* corrupt localStorage — pushLiveProgress falls back to now */ }
          setCurrentEventIndex(walkIdx);
          setCurrentHeat(walkHeat);
          pushLiveProgress(walkIdx, walkHeat);
          recalculateScheduleWithBothTimes(walkIdx, eventStartTime, eventEndTime);
          toast.info(`Caught up: ${ev.name} (${ev.gender}), Heat ${walkHeat}`);
        }
      } else if (calculatedHeat > currentHeat && calculatedHeat <= totalHeats) {
        // Advance to next heat within same event.
        //
        // Cold-restart partial-heat preservation: when the owner reopens
        // the app mid-meet and `calculatedHeat` jumps forward (e.g., from
        // saved heat 2 to actual heat 4), the new heat actually started
        // (calculatedHeat-1) * durationPerHeat after the event start —
        // NOT now. Pre-write the precise heatStartedAt so:
        //   (a) the localStorage-write effect sees a matching tuple and
        //       skips its "stamp now" overwrite.
        //   (b) pushLiveProgress reuses the precise stamp on its push,
        //       so spectators sync to the correct partial-heat moment.
        // Without this, the spectator countdown resets to full heat
        // duration on every cold-restart heat-advance.
        const heatStartInstant = addMinutes(eventStartTime, (calculatedHeat - 1) * durationPerHeat);
        try {
          const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
            liveMeet.currentHeat = calculatedHeat;
            liveMeet.heatTuple = `${currentEventIndex}:${calculatedHeat}`;
            liveMeet.heatStartedAt = heatStartInstant.toISOString();
            localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
          }
        } catch (_) { /* corrupt localStorage — pushLiveProgress falls back to now */ }
        setCurrentHeat(calculatedHeat);
        pushLiveProgress(currentEventIndex, calculatedHeat);

        toast.info(`Heat ${calculatedHeat} of ${totalHeats}`);
      }
    };

    // Run immediately
    updateByTime();

    // Check every 1 second
    const interval = setInterval(updateByTime, 1000);

    return () => clearInterval(interval);
  }, [isHydrated, isStarted, schedule, currentEventIndex, currentHeat, meetId, meetComplete]);

  // Spectator-side timeline — fully deterministic from (server anchor +
  // elapsed time + schedule). The server reports "the owner last anchored at
  // (event_idx, heat) at current_event_started_at". From there, we walk
  // forward through the schedule locally, every second. This gives correct
  // behavior regardless of whether the owner's app is foregrounded, locked,
  // backgrounded, or completely offline — and even survives the spectator's
  // own network outages because the math only needs the last cached anchor.
  useEffect(() => {
    if (isOwner) return; // owner has its own countdown effect above
    if (!isStarted || !schedule.length) return;
    if (meetComplete) return;

    const anchor = liveServerSnapshot;
    if (!anchor || !anchor.current_event_started_at) {
      setCountdown('');
      return;
    }
    const anchorStartedMs = new Date(anchor.current_event_started_at).getTime();
    if (isNaN(anchorStartedMs)) {
      setCountdown('');
      return;
    }
    const anchorEventIdx =
      typeof anchor.current_event_index === 'number' ? anchor.current_event_index : 0;
    const anchorHeat =
      typeof anchor.current_heat === 'number' ? anchor.current_heat : 1;

    const tick = () => {
      const nowOnServerClock = Date.now() + clockSkewMs;
      // Total time elapsed since the owner's last anchor (manual skip or
      // meet start). We walk this through successive heats below.
      let unconsumedMs = nowOnServerClock - anchorStartedMs;
      if (unconsumedMs < 0) unconsumedMs = 0;

      let displayIdx = anchorEventIdx;
      let displayHeat = anchorHeat;
      let heatStartOffsetMs = 0; // ms into the current heat once we land

      // Walk forward heat-by-heat through the schedule until we land in the
      // heat the spectator should currently be on. Bounded loop — schedules
      // are at most a few hundred heats, so a while is fine.
      // Using an explicit guard to avoid any pathological infinite loop.
      // Threshold past which a walk-forward overshoot is treated as
      // "the owner abandoned the meet" rather than "the owner is running
      // slow". 30 minutes is forgiving for real-life delays (athletes
      // taking longer than predicted, between-event chatter, etc.) while
      // catching the case where the owner went offline hours ago and
      // never resurfaced — at which point the meet is effectively over.
      const ABANDONED_OVERSHOOT_MS = 30 * 60 * 1000;
      let overshotByMs = 0;
      let safety = 1000;
      while (safety-- > 0) {
        const ev = schedule[displayIdx];
        if (!ev) {
          // Past the end of the schedule. The unconsumed remainder tells us
          // by how much we overshot. Small overshoot ⇒ owner is just slow
          // ⇒ clamp to last heat. Large overshoot ⇒ meet was abandoned
          // hours ago ⇒ flip to complete locally so spectators stop being
          // told "LIVE" forever.
          const lastIdx = schedule.length - 1;
          if (lastIdx < 0) return;
          displayIdx = lastIdx;
          displayHeat = schedule[lastIdx].heats || 1;
          heatStartOffsetMs = ((schedule[lastIdx].duration || 0) / (schedule[lastIdx].heats || 1)) * 60 * 1000;
          overshotByMs = unconsumedMs;
          break;
        }
        const totalHeats = ev.heats || 1;
        const heatDurMs = ((ev.duration || 0) / totalHeats) * 60 * 1000;
        if (heatDurMs <= 0) {
          // Defensive: zero-duration heat — just advance to avoid sticking.
          if (displayHeat < totalHeats) displayHeat += 1;
          else { displayIdx += 1; displayHeat = 1; }
          continue;
        }
        if (unconsumedMs < heatDurMs) {
          heatStartOffsetMs = unconsumedMs;
          break;
        }
        unconsumedMs -= heatDurMs;
        if (displayHeat < totalHeats) {
          displayHeat += 1;
        } else {
          displayIdx += 1;
          displayHeat = 1;
        }
      }

      const ev = schedule[displayIdx];
      if (!ev) {
        // Same rationale as above: never flip completion locally. Just bail
        // and let the next poll resolve the anchor.
        return;
      }

      // Push computed indices into the existing UI state so the rest of the
      // page (current-event card, banner, etc.) renders the right item.
      setCurrentEventIndex((prev) => (prev === displayIdx ? prev : displayIdx));
      setCurrentHeat((prev) => (prev === displayHeat ? prev : displayHeat));

      // If the walk-forward overshot the schedule end by more than the
      // grace window, the owner clearly abandoned the meet hours ago
      // (offline + never came back to tap End Meet). Flip the spectator
      // banner to complete so we don't keep showing "LIVE" indefinitely.
      // This is the only spectator-side path that flips meetComplete; the
      // server's ended_at remains the primary signal — this is a fallback.
      if (overshotByMs > ABANDONED_OVERSHOOT_MS) {
        setMeetComplete(true);
        setCountdown('');
        return;
      }

      const totalHeats = ev.heats || 1;
      const heatDurMs = ((ev.duration || 0) / totalHeats) * 60 * 1000;
      const remainingMs = heatDurMs - heatStartOffsetMs;
      const isLastHeatOfLastEvent =
        displayHeat >= totalHeats && displayIdx >= schedule.length - 1;

      if (isLastHeatOfLastEvent) {
        setCountdown(`Last ${vocab.heat} of Meet!`);
        return;
      }
      if (remainingMs > 0) {
        const totalSecs = Math.floor(remainingMs / 1000);
        const mins = Math.floor(totalSecs / 60);
        const secs = totalSecs % 60;
        const isLastHeat = displayHeat >= totalHeats;
        const label = isLastHeat ? `Next ${vocab.event.toLowerCase()} in about` : `Next ${vocab.heat.toLowerCase()} in about`;
        setCountdown(`${label} ${mins}:${secs.toString().padStart(2, '0')}`);
      } else {
        setCountdown('Advancing...');
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [
    isOwner,
    isStarted,
    schedule,
    meetComplete,
    liveServerSnapshot,
    clockSkewMs,
  ]);

  // Handle browser back button and navigation away from live meet
  useEffect(() => {
    if (!isStarted) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You have a live meet in progress. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isStarted]);

  // Custom navigation handler that shows confirmation for live meets
  const handleNavigation = (path) => {
    // Don't show warning if meet is complete - it's already ended
    if (isStarted && !meetComplete) {
      setPendingNavigation(path);
      setExitConfirmDialog(true);
    } else {
      navigate(path);
    }
  };

  const confirmExit = () => {
    // Keep live meet status - don't clear it so user can return
    setExitConfirmDialog(false);
    if (pendingNavigation === -1) {
      navigate(-1);
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleBack = () => {
    // Walk one step back in browser history. The wizard pushes a real history
    // entry per step, so this returns the user to the previous screen they
    // came from (Review, Edit Meet Details, Dashboard, etc.). The mid-meet
    // exit-confirmation flow still guards against accidentally leaving a
    // running meet.
    if (isOwner && isStarted && !meetComplete) {
      setPendingNavigation(-1);
      setExitConfirmDialog(true);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Generate schedule from meet data - returns the schedule length
  // Pure builder — returns the schedule array without touching state. Lets
  // callers build the array, optionally mutate it (e.g. re-anchor for a
  // live meet), then setSchedule exactly ONCE with the final values. This
  // is what eliminates the cold-restart race that previously required a
  // setTimeout(10ms) in the live-restoration effect.
  const buildScheduleItems = (meetData) => {
    const timeBetweenEventsStr = getTimeBetweenEvents(meetData.type);
    const timeBetweenEvents = parseFloat(timeBetweenEventsStr) || 0;

    let currentTime = new Date(`${meetData.date.split('T')[0]}T${meetData.startTime}`);
    const scheduleItems = [];
    let eventNumber = 1;

    meetData.events.forEach((event) => {
      const genderOrder = [];
      if (event.showGirls && event.showBoys) {
        const girlsFirst = (event.girlsOrder || 0) <= (event.boysOrder || 0);
        genderOrder.push(girlsFirst ? 'girls' : 'boys', girlsFirst ? 'boys' : 'girls');
      } else if (event.showGirls) {
        genderOrder.push('girls');
      } else if (event.showBoys) {
        genderOrder.push('boys');
      } else if (event.combined) {
        genderOrder.push('combined');
      }

      genderOrder.forEach(gender => {
        let heats, duration;

        if (gender === 'combined') {
          heats = event.girlsHeats || event.boysHeats || event.heats || 1;
          duration = event.girlsDuration || event.boysDuration || event.duration || '03:00';
        } else if (gender === 'girls') {
          heats = event.girlsHeats || event.heats || 1;
          duration = event.girlsDuration || event.duration || '03:00';
        } else {
          heats = event.boysHeats || event.heats || 1;
          duration = event.boysDuration || event.duration || '03:00';
        }

        const [min, sec] = duration.split(':').map(Number);
        const totalMinutes = (min + sec / 60) * heats;

        const genderLabel = gender === 'girls' ? 'Girls' : gender === 'boys' ? 'Boys' : 'Combined';
        const isCollege = meetData.level === 'college' || meetData.level === 'professional';
        const displayGender = isCollege ?
          (gender === 'girls' ? 'Women' : gender === 'boys' ? 'Men' : 'Combined') :
          genderLabel;

        scheduleItems.push({
          number: eventNumber++,
          name: event.name,
          gender: displayGender,
          heats,
          duration: totalMinutes,
          startTime: new Date(currentTime),
          endTime: addMinutes(currentTime, totalMinutes),
        });

        currentTime = addMinutes(currentTime, totalMinutes + timeBetweenEvents);
      });
    });

    return scheduleItems;
  };

  // Thin wrapper kept for the non-live (pre-start) path: build items then
  // set state in one go.
  const generateScheduleFromMeet = (meetData) => {
    const items = buildScheduleItems(meetData);
    setSchedule(items);
    return items.length;
  };

  const handleStartMeet = async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Starting a meet creates a server-side live_meet doc that spectators
      // poll. Without that doc, the spectator URL is dead even after the
      // owner shares it post-reconnect. Block the start until online so
      // we never produce broken share URLs downstream.
      criticalToast('You\'re offline. Reconnect to start the meet.');
      return;
    }
    // Even online, the meet may still be in the offline-sync queue. If
    // the meet doc isn't on the server, starting it produces a live_meet
    // pointing at a non-existent meet → spectator gets "Meet not found".
    const exists = await verifyMeetOnServer();
    if (!exists) {
      criticalToast('Meet is still syncing to the cloud. Try again in a few seconds.');
      return;
    }
    startMeet();
  };

  const startMeet = async () => {
    try {
      // First check if there's a blocking meet
      const checkResponse = await fetch(`${API_URL}/api/live-meet/check-active`);
      const checkData = await checkResponse.json();

      if (checkData.has_active_meet && checkData.active_meet_id !== meetId) {
        // There's a blocking meet - show force end dialog
        setBlockingMeetId(checkData.active_meet_id);
        setForceEndDialog(true);
        return;
      }

      // No blocking meet, proceed to start
      const response = await fetch(`${API_URL}/api/live-meet/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meet_id: meetId,
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorDetail = 'Failed to start meet';
        try {
          const errorData = JSON.parse(errorText);
          errorDetail = errorData.detail || errorDetail;
        } catch (e) {
          // Use default error message
        }
        toast.error(errorDetail);
        return;
      }

      setIsStarted(true);
      setManuallyEnded(false); // Reset the manually ended flag
      manuallyEndedRef.current = false;
      setMeetComplete(false); // clear any "Meet Complete" UI from a prior run
      setCurrentEventIndex(0);
      setCurrentHeat(1);
      // Reset the push refs so the first PUT on this new live cycle
      // carries a fresh current_event_started_at to spectators.
      confirmedTupleRef.current = null;
      pendingPushRef.current = null;
      pushLiveProgress(0, 1);

      // Recalculate schedule times from now
      recalculateScheduleFromEvent(0);

      // Also save to localStorage for dashboard display
      localStorage.setItem('foresight_live_meet', JSON.stringify({
        meetId: meetId,
        meetName: meet.name,
        venue: meet.venue,
        currentEventIndex: 0,
        currentHeat: 1,
        startedAt: new Date().toISOString()
      }));

      toast.success('Meet started! Tracking events.');
    } catch (error) {
      console.error('Start meet error:', error);
      toast.error('Failed to start meet. Please try again.');
    }
  };

  // Force end a blocking meet and start this one
  const handleForceEnd = async () => {
    try {
      // First, force end the blocking meet
      const endResponse = await fetch(`${API_URL}/api/live-meet/force-end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!endResponse.ok) {
        const errorData = await endResponse.json();
        toast.error(errorData.detail || 'Failed to force end. Please try again.');
        setForceEndDialog(false);
        return;
      }

      // Clear any stale localStorage
      localStorage.removeItem('foresight_live_meet');
      
      setForceEndDialog(false);
      setBlockingMeetId(null);
      
      toast.success('Previous meet ended. Starting new meet...');
      
      // Now start this meet
      setTimeout(() => {
        startMeet();
      }, 500);
      
    } catch (error) {
      console.error('Force end error:', error);
      toast.error('Failed to force end. Please try again.');
      setForceEndDialog(false);
    }
  };

  // Handle heat increment (next heat)
  const handleNextHeat = (e) => {
    e.stopPropagation(); // Prevent event card click
    if (!isStarted || currentEventIndex < 0) return;
    
    const currentEvent = schedule[currentEventIndex];
    if (!currentEvent) return;
    
    if (currentHeat < currentEvent.heats) {
      // Advance to next heat
      const newHeat = currentHeat + 1;
      setCurrentHeat(newHeat);
      pushLiveProgress(currentEventIndex, newHeat);
      
      // Calculate times based on new heat position
      const durationPerHeat = currentEvent.duration / currentEvent.heats;
      
      // Back-calculate start time: new heat means (newHeat - 1) heats have elapsed
      const elapsedHeats = newHeat - 1;
      const elapsedMinutes = elapsedHeats * durationPerHeat;
      const currentEventStartTime = addMinutes(new Date(), -elapsedMinutes);
      
      // Forward-calculate end time: remaining heats
      const remainingHeats = currentEvent.heats - newHeat + 1;
      const remainingMinutes = remainingHeats * durationPerHeat;
      const currentEventEndTime = addMinutes(new Date(), remainingMinutes);
      
      // Update schedule with BOTH start and end times
      recalculateScheduleWithBothTimes(currentEventIndex, currentEventStartTime, currentEventEndTime);
      
      // Update localStorage
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      liveMeet.currentHeat = newHeat;
      localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
      
      toast.success(`Heat ${newHeat} of ${currentEvent.heats}`);
    } else {
      // All heats complete - advance to next event
      if (currentEventIndex < schedule.length - 1) {
        const nextIndex = currentEventIndex + 1;
        setCurrentEventIndex(nextIndex);
        setCurrentHeat(1); // Reset to heat 1
        pushLiveProgress(nextIndex, 1);
        
        // Next event starts now
        recalculateScheduleFromEvent(nextIndex, new Date());
        
        // Update localStorage
        const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
        liveMeet.currentEventIndex = nextIndex;
        liveMeet.currentHeat = 1;
        localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
        
        const nextEvent = schedule[nextIndex];
        toast.success(`Now: ${nextEvent.name} (${nextEvent.gender})`);
      } else {
        toast.info(`Last ${vocab.heat.toLowerCase()} of last ${vocab.event.toLowerCase()}!`);
      }
    }
  };

  // Handle heat decrement (previous heat)
  const handlePrevHeat = (e) => {
    e.stopPropagation(); // Prevent event card click
    if (!isStarted || currentEventIndex < 0) return;
    
    const currentEvent = schedule[currentEventIndex];
    
    if (currentHeat > 1) {
      // Go back to previous heat
      const newHeat = currentHeat - 1;
      setCurrentHeat(newHeat);
      
      // Calculate times based on new heat position
      const durationPerHeat = currentEvent.duration / currentEvent.heats;
      
      // Back-calculate start time
      const elapsedHeats = newHeat - 1;
      const elapsedMinutes = elapsedHeats * durationPerHeat;
      const currentEventStartTime = addMinutes(new Date(), -elapsedMinutes);
      
      // Forward-calculate end time
      const remainingHeats = currentEvent.heats - newHeat + 1;
      const remainingMinutes = remainingHeats * durationPerHeat;
      const currentEventEndTime = addMinutes(new Date(), remainingMinutes);
      
      // Update schedule with BOTH start and end times
      recalculateScheduleWithBothTimes(currentEventIndex, currentEventStartTime, currentEventEndTime);
      
      // Update localStorage
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      liveMeet.currentHeat = newHeat;
      localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
      
      toast.success(`${vocab.heat} ${newHeat} of ${currentEvent.heats}`);
    } else if (currentEventIndex > 0) {
      // Go back to previous event's last heat
      const prevIndex = currentEventIndex - 1;
      const prevEvent = schedule[prevIndex];
      const newHeat = prevEvent.heats; // Last heat of previous event
      
      setCurrentEventIndex(prevIndex);
      setCurrentHeat(newHeat);
      
      // Calculate times for previous event at last heat
      const durationPerHeat = prevEvent.duration / prevEvent.heats;
      
      // Back-calculate start time
      const elapsedHeats = newHeat - 1;
      const elapsedMinutes = elapsedHeats * durationPerHeat;
      const prevEventStartTime = addMinutes(new Date(), -elapsedMinutes);
      
      // Forward-calculate end time (1 heat remaining)
      const remainingMinutes = durationPerHeat;
      const prevEventEndTime = addMinutes(new Date(), remainingMinutes);
      
      // Update schedule with BOTH start and end times
      recalculateScheduleWithBothTimes(prevIndex, prevEventStartTime, prevEventEndTime);
      
      // Update localStorage
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      liveMeet.currentEventIndex = prevIndex;
      liveMeet.currentHeat = newHeat;
      localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
      
      toast.success(`Back to: ${prevEvent.name} ${vocab.heat} ${newHeat}`);
    } else {
      toast.info(`Already at first ${vocab.heat.toLowerCase()} of first ${vocab.event.toLowerCase()}`);
    }
  };

  // Recalculate schedule times starting from a specific event at a given time
  // Uses the latest schedule from state via functional update to avoid closure issues
  const recalculateScheduleFromEvent = (startIndex, startTime = new Date()) => {
    if (!meet) return;
    
    // Get time between events based on meet type (indoor/outdoor)
    const timeBetweenEventsStr = getTimeBetweenEvents(meet.type);
    const timeBetweenEvents = parseFloat(timeBetweenEventsStr) || 0;
    
    // Use functional state update to ensure we're working with the latest schedule
    setSchedule(prevSchedule => {
      if (!prevSchedule.length) return prevSchedule;
      
      let currentTime = startTime;
      
      return prevSchedule.map((item, idx) => {
        if (idx < startIndex) {
          // Past events - keep their times
          return item;
        } else if (idx === startIndex) {
          // Current event - starts at the given time
          const endTime = addMinutes(startTime, item.duration);
          currentTime = addMinutes(endTime, timeBetweenEvents);
          return {
            ...item,
            startTime: new Date(startTime),
            endTime: endTime,
          };
        } else {
          // Future events - calculate from current time forward
          const eventStart = new Date(currentTime);
          const endTime = addMinutes(currentTime, item.duration);
          currentTime = addMinutes(endTime, timeBetweenEvents);
          return {
            ...item,
            startTime: eventStart,
            endTime: endTime,
          };
        }
      });
    });
  };
  
  // Recalculate schedule given the current event's END time
  // This is used when we know when the current event will finish (e.g., mid-event heat change)
  const recalculateScheduleFromCurrentProgress = (currentIndex, currentEventEndTime) => {
    if (!meet) return;
    
    // Get time between events based on meet type (indoor/outdoor)
    const timeBetweenEventsStr = getTimeBetweenEvents(meet.type);
    const timeBetweenEvents = parseFloat(timeBetweenEventsStr) || 0;
    
    // Use functional state update to ensure we're working with the latest schedule
    setSchedule(prevSchedule => {
      if (!prevSchedule.length) return prevSchedule;
      
      // Next event starts after current event ends + time between events
      let currentTime = addMinutes(currentEventEndTime, timeBetweenEvents);
      
      return prevSchedule.map((item, idx) => {
        if (idx <= currentIndex) {
          // Current and past events - keep their existing start times
          // But update current event's end time to reflect remaining heats
          if (idx === currentIndex) {
            return {
              ...item,
              endTime: new Date(currentEventEndTime),
            };
          }
          return item;
        } else {
          // Future events - calculate from current time forward
          const eventStart = new Date(currentTime);
          const endTime = addMinutes(currentTime, item.duration);
          currentTime = addMinutes(endTime, timeBetweenEvents);
          return {
            ...item,
            startTime: eventStart,
            endTime: endTime,
          };
        }
      });
    });
  };
  
  // Recalculate schedule with BOTH start and end times for current event
  // This is the key function for proper auto-advancement when manually changing heats
  const recalculateScheduleWithBothTimes = (currentIndex, currentEventStartTime, currentEventEndTime) => {
    if (!meet) return;
    
    const timeBetweenEventsStr = getTimeBetweenEvents(meet.type);
    const timeBetweenEvents = parseFloat(timeBetweenEventsStr) || 0;
    
    setSchedule(prevSchedule => {
      if (!prevSchedule.length) return prevSchedule;
      
      let nextStartTime = addMinutes(currentEventEndTime, timeBetweenEvents);
      
      return prevSchedule.map((item, idx) => {
        if (idx < currentIndex) {
          return item; // Past events unchanged
        } else if (idx === currentIndex) {
          // Current event - update BOTH start and end times
          return {
            ...item,
            startTime: new Date(currentEventStartTime),
            endTime: new Date(currentEventEndTime),
          };
        } else {
          // Future events
          const eventStart = new Date(nextStartTime);
          const eventEnd = addMinutes(nextStartTime, item.duration);
          nextStartTime = addMinutes(eventEnd, timeBetweenEvents);
          return {
            ...item,
            startTime: eventStart,
            endTime: eventEnd,
          };
        }
      });
    });
  };

  const handleEndMeetClick = () => {
    setEndMeetDialog(true);
  };

  const confirmEndMeet = async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-meet/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meet_id: meetId,
          user_id: userId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to end meet');
        setEndMeetDialog(false);
        return;
      }

      // Lock the UI into "completed" state. Without setting meetComplete=true
      // here, the local state flips back to "not started", which re-exposes
      // the Edit Meet and Start Meet buttons. A meet that has been ended must
      // not be editable or restartable — both for data integrity (spectators
      // would desync) and UX (real-world meets don't get re-run).
      setIsStarted(true);
      isStartedRef.current = true;
      setMeetComplete(true);
      setManuallyEnded(true);
      manuallyEndedRef.current = true;
      localStorage.removeItem('foresight_live_meet');
      // Persist the ended state so any subsequent mount (from dashboard,
      // deep-link, etc.) locks straight into completed mode.
      markMeetEnded(meetId);
      setEndMeetDialog(false);

      toast.success('Meet ended successfully!');
    } catch (error) {
      console.error('End meet error:', error);
      toast.error('Failed to end meet. Please try again.');
      setEndMeetDialog(false);
    }
  };

  // Auto-end meet when all events/heats are complete
  const autoEndMeet = async () => {
    // CRITICAL: do NOT clear `foresight_live_meet` from localStorage until
    // the backend has confirmed the end (2xx or 404). Previously we cleared
    // it eagerly here, which produced the May 2026 production repro: meet
    // running, device offline, walk-forward (or owner) calls autoEndMeet
    // → POST fails offline → live-meet pointer already deleted → user
    // cold-restarts app → Dashboard shows "no live meet" even though the
    // backend still considers it active and the spectator URL stays live.
    //
    // The new flow: mark the local pointer as `pendingEnd: true` and let
    // it linger. Dashboard's verify-live-meet check (which only purges on
    // explicit 404) treats it as resumable until the backend confirms or
    // the user manually re-enters and ends it. Once the queued end POST
    // succeeds, we clear the pointer here OR via the App-level periodic
    // flush.
    try {
      const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
      if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
        liveMeet.pendingEnd = true;
        liveMeet.pendingEndQueuedAt = new Date().toISOString();
        localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
      }
    } catch (_) { /* corrupt localStorage — fall through */ }

    // Remember this meet was ended so any later mount of this meet's view
    // locks straight into "completed" mode — no flash of the back arrow or
    // Edit Meet button that would let the owner re-open an already-finished
    // meet and apply an "update" the spectators wouldn't see.
    markMeetEnded(meetId);

    // Queue an end-of-meet intent BEFORE we attempt the network call. If the
    // device is offline (locked screen, no WiFi, app about to be force-quit),
    // the queued entry is what guarantees the backend eventually learns the
    // meet is over — picked up by the App-level retry on next online event
    // or app start.
    try {
      const queue = JSON.parse(localStorage.getItem('foresight_pending_end') || '[]');
      if (!queue.find((q) => q.meet_id === meetId)) {
        queue.push({ meet_id: meetId, user_id: userId, queued_at: new Date().toISOString() });
        localStorage.setItem('foresight_pending_end', JSON.stringify(queue));
      }
    } catch (_) { /* ignore */ }

    const onBackendConfirmed = () => {
      // Backend has accepted the end. Now (and only now) is it safe to
      // clear the live-meet pointer.
      try {
        const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
        if (liveMeet.meetId === meetId || String(liveMeet.meetId) === String(meetId)) {
          localStorage.removeItem('foresight_live_meet');
        }
      } catch (_) { /* ignore */ }
      try {
        const queue = JSON.parse(localStorage.getItem('foresight_pending_end') || '[]');
        const filtered = queue.filter((q) => q.meet_id !== meetId);
        if (filtered.length) {
          localStorage.setItem('foresight_pending_end', JSON.stringify(filtered));
        } else {
          localStorage.removeItem('foresight_pending_end');
        }
      } catch (_) { /* ignore */ }
    };

    try {
      // Try to end the meet in the backend
      const response = await fetch(`${API_URL}/api/live-meet/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meet_id: meetId,
          user_id: userId
        })
      });

      if (response.ok) {
        onBackendConfirmed();
        toast.success('Meet complete! Session ended automatically.');
      } else if (response.status === 404) {
        // Backend says no active meet with that id — someone else (or a
        // previous retry) already ended it. Treat as success.
        onBackendConfirmed();
        toast.success('Meet complete!');
      } else {
        // Other non-2xx: one immediate retry after 2s before walking away.
        // Catches transient backend hiccups (e.g. brief 5xx) without waiting
        // for the 30s queue-flush timer to pick it up. If the retry also
        // fails, the queued entry stays put and the periodic retry handles
        // it. Capture to Sentry so we can spot any pattern of these failures.
        SentryReact.captureMessage(
          `live-meet/end first attempt failed: ${response.status}`,
          'warning'
        );
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const retry = await fetch(`${API_URL}/api/live-meet/end`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ meet_id: meetId, user_id: userId }),
          });
          if (retry.ok || retry.status === 404) {
            onBackendConfirmed();
          } else {
            SentryReact.captureMessage(
              `live-meet/end retry also failed: ${retry.status}`,
              'error'
            );
          }
        } catch (_) {
          // Network failure on retry — leave queued + pointer in pendingEnd
          // state so the periodic App.js flush picks it up. Pointer stays
          // until backend confirms.
        }
        toast.success('Meet complete!');
      }
    } catch (error) {
      // Network failure — leave the queued entry alone. App-level retry
      // (online event / periodic flush / app start) will flush it.
      console.error('Auto-end meet error:', error);
      SentryReact.captureException(error);
      toast.success('Meet complete!');
    }
  };

  const formatTime = (date) => {
    return format(date, 'h:mm a');
  };

  // Handle meet not found
  if (meetNotFound) {
    return (
      <div className="min-h-screen bg-background">
        <Header showBack={true} onBack={() => navigate('/')} title="Meet Not Found" />
        <div className="flex flex-col items-center justify-center p-8 mt-20">
          <AlertTriangle className="w-16 h-16 text-orange-500 mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Meet Not Found</h2>
          <p className="text-muted-foreground text-center mb-6">
            This meet may have been deleted or is no longer available.
          </p>
          <Button onClick={() => navigate('/')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!meet) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading meet...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header.
          INVARIANT — DO NOT REGRESS:
          On a COMPLETED meet, no back arrow is rendered. The only path out
          is the "Back to Dashboard" button rendered below. Letting the
          owner navigate back from a completed meet has historically caused
          stale-state bugs (resume-live-meet banner re-appearing, edit
          attempts on a frozen meet, etc.) — gate BOTH the arrow's
          visibility AND its handler on !meetComplete.
          Spectators (isOwner === false) never see a back arrow — they
          arrived via a shared URL and shouldn't navigate inside the app. */}
      <Header
        showBack={isOwner && !meetComplete}
        onBack={isOwner && !meetComplete ? handleBack : undefined}
        title={meet.name}
      />

      {/* Live Meet Banner - Below header */}
      {isStarted && (
        <div className={cn(
          "sticky top-[52px] z-40 text-white px-4 py-1.5 shadow-lg",
          meetComplete ? "bg-blue-600" : "bg-green-600"
        )}>
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              {meetComplete ? (
                <>
                  <span className="text-sm">✅</span>
                  <span className="font-semibold text-sm">COMPLETE</span>
                  <span className="text-blue-200 text-sm">|</span>
                  <span className="text-xs">Meet finished</span>
                </>
              ) : (
                <>
                  <span className="animate-pulse text-sm">🔴</span>
                  <span className="font-semibold text-sm">LIVE</span>
                  <span className="text-green-200 text-sm">|</span>
                  <span className="text-xs">Meet in progress</span>
                </>
              )}
            </div>
            <div className="flex items-center text-xs">
              <div className={cn(
                "flex items-center gap-1.5 px-2.5 py-0.5 rounded-full",
                meetComplete ? "bg-blue-700" : "bg-green-700"
              )}>
                <Clock className="w-3.5 h-3.5" />
                {meetComplete ? (
                  <span>All {schedule.length} {vocab.events} complete</span>
                ) : (
                  <>
                    <span>{vocab.event} {currentEventIndex + 1}/{schedule.length}</span>
                    {schedule[currentEventIndex] && schedule[currentEventIndex].heats > 1 && (
                      <>
                        <span className="text-green-300">•</span>
                        <span>{vocab.heat} {currentHeat}/{schedule[currentEventIndex].heats}</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* No spacer needed now since banner is positioned properly */}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* Share Button — owner only. Spectators are recipients of the link,
              not distributors. */}
          {isOwner && (
            <div className="flex justify-end">
              <Button variant="outline" className="gap-2" onClick={handleShare} data-testid="meet-share-button">
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          )}

          {/* Meet Info Card */}
          <Card className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(meet.date), 'MMM dd, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span className="truncate">{meet.venue}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Starts {meet.startTime}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{schedule.length} {vocab.events}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* Indoor / Outdoor chip is hidden for XC (cross
                    country is exclusively outdoor — the chip would be
                    tautological). Track + Swim still render it. */}
                {meet.sport_type !== 'xc' && (
                  <Badge variant="outline" className="capitalize" data-testid="meet-header-type-chip">
                    {meet.type}
                  </Badge>
                )}
                <Badge variant="outline" className="capitalize">
                  {meet.level.replace('-', ' ')}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Action Buttons - Owner only: Start/Edit Meet */}
          {isOwner && !isStarted && (
            <div className="flex gap-4">
              <Button variant="outline" size="lg" className="flex-1" onClick={() => handleNavigation(`/edit-meet/${meetId}?edit=true`)}>
                <Edit className="w-5 h-5 mr-2" />
                Edit Meet
              </Button>
              <Button
                size="lg"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all border-0"
                onClick={handleStartMeet}
              >
                <Play className="w-5 h-5 mr-2 fill-white" />
                Start Meet
              </Button>
            </div>
          )}

          {/* Action Buttons - Owner only: Edit/End Meet when live */}
          {isOwner && isStarted && !meetComplete && (
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                size="lg" 
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50" 
                onClick={handleEndMeetClick}
              >
                End Meet
              </Button>
            </div>
          )}

          {/* Action Buttons — owner only, shown when meet is complete.
              Once a meet has finished, the only action is to leave the
              page. Resetting / re-editing is intentionally disabled because
              real-world meets don't get re-run, and re-editing would
              desync any spectator browsers that already loaded the meet. */}
          {isOwner && isStarted && meetComplete && (
            <div className="flex">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={() => navigate('/')}
                data-testid="meet-complete-back-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          )}

          {/* Event Schedule */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">{vocab.event} Schedule</h2>
            <div className="space-y-2">
              {schedule.map((item, index) => {
                const isCurrentEvent = isStarted && index === currentEventIndex;
                const isPastEvent = isStarted && index < currentEventIndex;
                const isFutureEvent = isStarted && index > currentEventIndex;
                const isLastEvent = index === schedule.length - 1;
                
                // Determine gender for color coding
                const isGirls = item.gender === 'Girls' || item.gender === 'Women';
                const isBoys = item.gender === 'Boys' || item.gender === 'Men';

                return (
                  <div
                    key={index}
                    data-testid={`event-card-${index}`}
                    className={cn(
                      'rounded-lg border transition-all',
                      // Current event takes priority - green highlight
                      isCurrentEvent && 'bg-green-100 border-green-600 border-2 shadow-xl ring-4 ring-green-200',
                      // Past events - gray
                      isPastEvent && 'bg-gray-100 opacity-70 border-gray-300',
                      // Future/not started events - gender color coded
                      !isCurrentEvent && !isPastEvent && isGirls && 'gender-girls',
                      !isCurrentEvent && !isPastEvent && isBoys && 'gender-boys',
                      !isCurrentEvent && !isPastEvent && !isGirls && !isBoys && 'bg-white border-gray-200',
                      // Hover states (only when meet not started for preview)
                      !isStarted && 'hover:shadow-md cursor-pointer'
                    )}
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Event Number */}
                      <div className={cn(
                        'flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm flex-shrink-0',
                        isCurrentEvent && 'bg-green-600 text-white shadow-md',
                        isPastEvent && 'bg-gray-400 text-white',
                        !isCurrentEvent && !isPastEvent && isGirls && 'bg-pink-500 text-white',
                        !isCurrentEvent && !isPastEvent && isBoys && 'bg-blue-500 text-white',
                        !isCurrentEvent && !isPastEvent && !isGirls && !isBoys && 'bg-purple-500 text-white'
                      )}>
                        {item.number}
                      </div>

                      {/* Event Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={cn(
                            "font-semibold",
                            // The card background uses literal tailwind
                            // utilities (bg-green-100 / bg-gray-100 /
                            // bg-white) that stay LIGHT in dark mode, so
                            // theme-aware `text-foreground` (white-on-dark)
                            // becomes unreadable white-on-light. Force a
                            // dark text colour on those three branches.
                            // gender-girls / gender-boys cards have proper
                            // dark-mode variants of their background, so
                            // they keep the default text-foreground.
                            (isCurrentEvent || isPastEvent || (!isGirls && !isBoys))
                              ? "text-gray-900"
                              : "text-foreground"
                          )}>{item.name}</h3>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "text-xs",
                              isGirls && "bg-pink-200 text-pink-800 hover:bg-pink-200",
                              isBoys && "bg-blue-200 text-blue-800 hover:bg-blue-200",
                              !isGirls && !isBoys && "bg-purple-200 text-purple-800 hover:bg-purple-200"
                            )}
                          >
                            {item.gender}
                          </Badge>
                        </div>
                        <p className={cn(
                          "text-sm mt-1",
                          (isCurrentEvent || isPastEvent || (!isGirls && !isBoys))
                            ? "text-gray-600"
                            : "text-muted-foreground"
                        )}>
                          {item.heats} heat{item.heats > 1 ? 's' : ''} • {Math.round(item.duration)}min total
                        </p>
                      </div>

                      {/* Time Display */}
                      <div className="text-right flex-shrink-0">
                        {isPastEvent ? (
                          <p className="text-sm font-medium text-gray-500">Done</p>
                        ) : isCurrentEvent ? (
                          null
                        ) : (() => {
                          // Spectators prefer the server-pushed schedule
                          // because it reflects the owner's recalculated
                          // wall-clock times after every skip. Owners keep
                          // reading straight from `item` (their local state
                          // is already the freshest source of truth).
                          const serverSlot = (!isOwner && liveServerSnapshot && Array.isArray(liveServerSnapshot.schedule))
                            ? liveServerSnapshot.schedule[index]
                            : null;
                          const displayStart = serverSlot?.startTime || item.startTime;
                          const displayEnd = serverSlot?.endTime || item.endTime;
                          return (
                            <>
                              <p className={cn(
                                "text-sm font-medium",
                                !isGirls && !isBoys ? "text-gray-900" : "text-foreground"
                              )}>
                                {formatTime(displayStart)}
                              </p>
                              <p className={cn(
                                "text-xs",
                                !isGirls && !isBoys ? "text-gray-600" : "text-muted-foreground"
                              )}>
                                {formatTime(displayEnd)}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Heat Controls - Only for current event */}
                    {isCurrentEvent && (
                      <div className="px-4 pb-4 pt-2 border-t border-green-300">
                        {/* Current Heat Display */}
                        <div className="text-center mb-3">
                          <span className="text-lg text-green-600 mr-1">About</span>
                          <span className="text-4xl font-bold text-green-700">{vocab.heat} {currentHeat}</span>
                          <span className="text-xl text-green-600 ml-2">of {item.heats}</span>
                        </div>
                        
                        {/* Countdown Timer */}
                        {(countdown || meetComplete) && (
                          <div className={cn(
                            "text-center mb-4 py-2 px-4 rounded-lg font-semibold",
                            meetComplete
                              ? "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-300"
                              : countdown === 'Advancing...' 
                              ? "bg-yellow-100 text-yellow-800 animate-pulse" 
                              : countdown === `Last ${vocab.heat} of Meet!`
                              ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-300"
                              : "bg-green-50 text-green-700"
                          )}>
                            <span className="text-lg">{meetComplete ? 'Meet Complete!' : countdown}</span>
                          </div>
                        )}
                        
                        {/* Heat Navigation Buttons — OWNER ONLY. Spectators
                            only watch; they must never see controls that
                            would let them advance the coach's live meet. */}
                        {isOwner && !meetComplete && (
                          <div className="flex items-center justify-center gap-3">
                            {/* Previous Heat Button */}
                            <button
                              onClick={handlePrevHeat}
                              disabled={currentHeat === 1 && currentEventIndex === 0}
                              className={cn(
                                "flex items-center gap-2 px-5 py-3 rounded-xl shadow-sm active:scale-95 transition-all",
                                currentHeat === 1 && currentEventIndex === 0
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-white hover:bg-gray-50 border-2 border-gray-300 text-gray-700"
                              )}
                              data-testid="prev-heat-btn"
                            >
                              <ChevronUp className="w-5 h-5" />
                              <span className="font-semibold">Prev {vocab.heat}</span>
                            </button>

                            {/* Next Heat Button - disabled on last heat */}
                            <button
                              onClick={handleNextHeat}
                              disabled={currentHeat === item.heats}
                              className={cn(
                                "flex items-center gap-2 px-5 py-3 rounded-xl shadow-lg active:scale-95 transition-all",
                                currentHeat === item.heats
                                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                  : "bg-green-600 hover:bg-green-700 text-white"
                              )}
                              data-testid="next-heat-btn"
                            >
                              <span className="font-semibold">Next {vocab.heat}</span>
                              <ChevronDown className="w-5 h-5" />
                            </button>
                          </div>
                        )}

                        {/* Skip to Next Event Button — OWNER ONLY. */}
                        {isOwner && !meetComplete && !isLastEvent && (
                          <div className="mt-3 flex justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Skip to next event
                                const nextIndex = currentEventIndex + 1;
                                setCurrentEventIndex(nextIndex);
                                setCurrentHeat(1);
                                recalculateScheduleFromEvent(nextIndex, new Date());
                                
                                const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
                                liveMeet.currentEventIndex = nextIndex;
                                liveMeet.currentHeat = 1;
                                localStorage.setItem('foresight_live_meet', JSON.stringify(liveMeet));
                                
                                const nextEvent = schedule[nextIndex];
                                toast.success(`Skipped to: ${nextEvent.name} (${nextEvent.gender})`);
                              }}
                              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow active:scale-95 transition-all"
                              data-testid="next-event-btn"
                            >
                              <span className="font-medium">Skip to Next {vocab.event}</span>
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Branding footer - links spectators to the marketing site */}
          <footer
            className="mt-8 mb-2 flex flex-col items-center justify-center text-xs text-gray-500 gap-3"
            data-testid="meet-branding-footer"
          >
            <span>
              Powered by Foresight Meet Schedule Predictor —{' '}
              <a
                href="https://foresightpulse.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                data-testid="meet-branding-footer-link"
              >
                foresightpulse.com
              </a>
            </span>

            {/* Google Play badge — spectator view only.
                Native owners are already inside the Android app, so the
                badge is gated off for them. Flexbox padding equals ~1/4
                of the badge's rendered height (py-3 ≈ 12px around a ~48px
                badge) so the badge feels deliberately set off from the
                schedule above it. max-w-[180px] keeps it as a subtle
                footer element across both iPhone (~390px) and Android
                (~360-412px) widths. */}
            {!isOwner && (
              <div
                className="flex flex-col items-center justify-center w-full py-3"
                data-testid="play-store-footer"
              >
                <p
                  className="text-xs text-gray-500 text-center max-w-[260px] pt-2 pb-2 leading-snug"
                  data-testid="play-store-footer-caption"
                >
                  To create and manage your own live meet schedules, get the coordinator app:
                </p>
                <a
                  href="https://play.google.com/store/apps/details?id=com.foresightpulse.track&pcampaignid=web_share&pli=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  data-testid="play-store-footer-link"
                  aria-label="Get Foresight Meet Schedule Predictor on Google Play"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="block max-w-[180px] w-full h-auto"
                    loading="lazy"
                    data-testid="play-store-footer-badge"
                  />
                </a>
              </div>
            )}
          </footer>
        </div>
      </main>

      {/* Exit Confirmation Dialog */}
      <Dialog open={exitConfirmDialog} onOpenChange={setExitConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Leave Live Meet?
            </DialogTitle>
            <DialogDescription>
              You have a meet in progress. If you leave now, tracking will pause but you can return from the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Card className="p-4 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Tip:</strong> Your live meet will appear on the dashboard with a &quot;LIVE&quot; badge so you can quickly return to it.
              </p>
            </Card>
          </div>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setExitConfirmDialog(false)} className="flex-1">
              Stay Here
            </Button>
            <Button variant="destructive" onClick={confirmExit} className="flex-1">
              Leave Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Meet Confirmation Dialog */}
      <Dialog open={endMeetDialog} onOpenChange={setEndMeetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              End Live Meet?
            </DialogTitle>
            <DialogDescription>
              This action will end the live meet tracking session.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Card className="p-4 bg-red-50 border-red-200">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Ending this meet is final. To run another meet you'll need to create a new meet schedule.
              </p>
            </Card>
          </div>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setEndMeetDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmEndMeet} className="flex-1">
              End Meet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force End Blocking Meet Dialog */}
      <Dialog open={forceEndDialog} onOpenChange={setForceEndDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Another Meet is Active
            </DialogTitle>
            <DialogDescription>
              A previous meet session was not properly ended and is blocking this meet from starting.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Card className="p-4 bg-red-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">⚠️ CAUTION</p>
                  <p className="text-sm text-red-700 mt-1">
                    Force ending will immediately terminate the other meet session. Any unsaved progress from that meet will be lost.
                  </p>
                </div>
              </div>
            </Card>
            {blockingMeetId && (
              <p className="text-xs text-muted-foreground text-center">
                Blocking meet ID: {blockingMeetId}
              </p>
            )}
          </div>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setForceEndDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleForceEnd} className="flex-1">
              Force End &amp; Start This Meet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share Meet
            </DialogTitle>
            <DialogDescription>
              Share this meet schedule with others. They can view the event times and schedule.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Meet Info Preview */}
            <Card className="p-4 bg-muted/50">
              <h3 className="font-semibold text-foreground">{meet?.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {meet?.venue} • {meet?.date ? format(new Date(meet.date), 'MMM dd, yyyy') : ''} • {meet?.startTime}
              </p>
            </Card>

            {/* Share Link */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Share Link</Label>
              <div className="flex gap-2">
                <Input 
                  value={getShareUrl()}
                  readOnly
                  className="flex-1 bg-muted text-sm"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={copyShareLink}
                  className={cn(
                    "flex-shrink-0 transition-colors",
                    linkCopied && "bg-green-100 border-green-300 text-green-700"
                  )}
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShareDialog(false)} className="flex-1">
              Close
            </Button>
            <Button onClick={copyShareLink} className="flex-1 gap-2">
              {linkCopied ? <Check className="w-4 h-4" /> : <Link className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Copy Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
